// Page Me — number, time and status formatting. Pure; no DOM, no Vue.

export const OPERATOR_NUMBER = '5550000411';

/** Status → the letter mark drawn on the LCD / list rows. Letters first, colour second. */
export const STATUS_MARK = Object.freeze({
  queued: '…',
  network: '✓',
  delivered: 'D',
  expired: 'X',
  failed: 'X',
});

/** Status → the word a screen reader hears. */
export const STATUS_WORD = Object.freeze({
  queued: 'Queued',
  network: 'Sent',
  delivered: 'Delivered',
  expired: 'Expired',
  failed: 'Not delivered',
});

const MONTHS = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
const pad = (n) => String(n).padStart(2, '0');

/** "5553829104" → "555-382-9104". Anything else is returned as given. */
export function formatNumber(n) {
  const s = String(n ?? '').replace(/\D/g, '');
  if (s.length !== 10) return String(n ?? '');
  return `${s.slice(0, 3)}-${s.slice(3, 6)}-${s.slice(6)}`;
}

/**
 * What somebody typed → 10 digits, or null.
 * Accepts "555-382-9104", "(555) 382 9104", "+1 555 382 9104", "3829104" (the
 * 555 is implied for a bare 7-digit number). Must be a 555 number.
 */
export function parseNumberInput(raw) {
  let s = String(raw ?? '').replace(/[\s\-().+]/g, '');
  if (!/^\d+$/.test(s)) return null;
  if (s.length === 11 && s.startsWith('1')) s = s.slice(1);
  if (s.length === 7) s = `555${s}`;
  return /^555\d{7}$/.test(s) ? s : null;
}

/** 1727192520000 → "3:42 PM" (local time). */
export function formatTime(ts) {
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return '';
  const h = d.getHours();
  return `${h % 12 || 12}:${pad(d.getMinutes())} ${h < 12 ? 'AM' : 'PM'}`;
}

/** 1727192520000 → "24 SEP 15:42" (local time, LCD voice). */
export function formatStamp(ts) {
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getDate()} ${MONTHS[d.getMonth()]} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** 1727192520000 → "2026-09-24 15:42" — the export file's timestamp. */
export function formatIso(ts) {
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// ── names first ─────────────────────────────────────────────────────────────
//
// THE DISPLAY RULE, everywhere a person appears (lists, thread header, To,
// the LCD's FROM line, pending requests, group members, announcements):
//
//   the name you saved for them  ▸  the name they chose (handle)  ▸  their number
//
// Lists put that name first and `secondaryLine()` under it, so the number is
// always one glance away but never the headline once there is a name.

/** A person's display name. `friend` may be a Friend, a group member `{ number, handle }`, or undefined. */
export function displayName(friend, number) {
  const name = String(friend?.name ?? '').trim();
  if (name) return name;
  const handle = String(friend?.handle ?? '').trim();
  if (handle) return handle;
  return formatNumber(friend?.number ?? number ?? '');
}

/**
 * The quieter line under a name in a list: their number — and the handle they
 * chose, when you saved them under a different name. '' when the name IS the
 * number (nothing to add).
 */
export function secondaryLine(friend, number) {
  const n = friend?.number ?? number ?? '';
  const num = formatNumber(n);
  if (displayName(friend, n) === num) return '';
  const name = String(friend?.name ?? '').trim();
  const handle = String(friend?.handle ?? '').trim();
  const parts = [];
  if (name && handle && name.toUpperCase() !== handle.toUpperCase()) parts.push(handle);
  parts.push(num);
  return parts.join(' · ');
}

/** Three letters for an LCD chip: "Mum" → "MUM", "555-214-0987" → "555". */
export function chipText(label) {
  return String(label ?? '').replace(/[^A-Za-z0-9]/g, '').slice(0, 3).toUpperCase() || '#';
}

/**
 * A sent group page's mark: `D 3/5` once any copy is delivered, else the
 * page's own mark (… ✓ X). Plain pages get their usual mark.
 */
export function deliveryMark(page) {
  const n = Number(page?.recipients) || 0;
  const mark = STATUS_MARK[page?.status] ?? '';
  if (!page?.groupId || !n || page.status === 'queued' || page.status === 'failed') return mark;
  const got = (page.deliveredBy ?? []).length;
  if (!got) return page.status === 'expired' ? mark : STATUS_MARK.network;
  return `D ${got}/${n}`;
}

/** The words for `deliveryMark` (screen readers). */
export function deliveryWord(page) {
  const n = Number(page?.recipients) || 0;
  const word = STATUS_WORD[page?.status] ?? '';
  if (!page?.groupId || !n || page.status === 'queued' || page.status === 'failed') return word;
  const got = (page.deliveredBy ?? []).length;
  if (!got) return page.status === 'expired' ? word : STATUS_WORD.network;
  return `Delivered to ${got} of ${n}`;
}
